import React from 'react';
import { Form, FormInstance, Radio, Space } from 'antd';
import {
  ErrorHandler,
  InfoTooltip,
  LoadingState,
  TextArea,
} from '@common/components';
import { useQuery } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import {
  CancelAssignmentQuery,
  CancelAssignmentResponse,
} from 'works/legacy/graphql/staffing-request-assignment';
import { Store } from 'antd/lib/form/interface';
import { ENUM_QUERY } from '@common/graphql';

export function CancelAssignmentForm({
  form,
  onSubmit,
  assignmentId,
  redirectToWorkerStatusSetup,
  setRedirectToWorkerStatusSetup,
}: {
  form: FormInstance;
  onSubmit: (values: Store) => void;
  redirectToWorkerStatusSetup: boolean;
  setRedirectToWorkerStatusSetup: (value: boolean) => void;
  assignmentId?: string;
}) {
  const currentMembership = useCurrentMembership();

  const {
    data: enumData,
    error: enumError,
    loading: enumLoading,
  } = useQuery(ENUM_QUERY, {
    variables: {
      key: 'AssignmentCancelledReason',
    },
  });

  const {
    data: assignmentData,
    loading: assignmentLoading,
    error: assignmentError,
  } = useQuery<CancelAssignmentResponse>(CancelAssignmentQuery, {
    variables: {
      id: assignmentId,
    },
    skip: !assignmentId,
  });

  const cancelReasons = enumData?.enum?.map((cancelReason) => ({
    key: cancelReason.name,
    value: cancelReason.name,
    label: cancelReason.description,
  }));

  const assignment = assignmentData?.staffingRequestAssignment;
  const identificationCode = assignment?.worksProfile.identificationCode;

  const anyError = enumError || assignmentError;
  if (anyError) {
    return <ErrorHandler error={anyError} log="CancelOptions" />;
  }

  const isLoading =
    enumLoading || assignmentLoading || !cancelReasons || !currentMembership;
  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onSubmit}
      initialValues={{
        reason: cancelReasons[0].value,
        workerStatus: 'eligible_for_resubmission',
      }}
    >
      <Form.Item
        name="reason"
        label="Category"
        rules={[
          {
            required: true,
            message: 'Select a category',
          },
        ]}
      >
        <Radio.Group>
          <Space direction="vertical">
            {cancelReasons.map((cancelReason) => (
              <Radio key={cancelReason.key} value={cancelReason.value}>
                <span>{cancelReason.label}</span>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="details"
        label="Comment"
        rules={[
          {
            type: 'string',
            min: 1,
            max: 1000,
            required: true,
            message: 'Please enter a comment',
          },
        ]}
      >
        <TextArea
          rows={2}
          placeholder="Please provide more details..."
          maxLength={1000}
        />
      </Form.Item>

      {identificationCode && (
        <Form.Item
          name="workerStatus"
          label={
            <>
              Worker Status
              <InfoTooltip
                size="md"
                leftOffset="lg"
                title="You can also edit this at a later time."
              />
            </>
          }
          rules={[
            {
              required: true,
              message: 'Select a status',
            },
          ]}
        >
          <Radio.Group
            onChange={() => {
              setRedirectToWorkerStatusSetup(!redirectToWorkerStatusSetup);
            }}
            disabled={!currentMembership.canUpdateWorkerStatus}
          >
            <Space direction="vertical">
              <Radio
                key="eligible_for_resubmission"
                value="eligible_for_resubmission"
              >
                Eligible for Resubmission
              </Radio>
              <Radio key="do_not_rehire" value="do_not_rehire">
                <>
                  Do Not Rehire
                  <InfoTooltip
                    size="md"
                    leftOffset="lg"
                    title="Suppliers will need to provide specific reasons if they choose to resubmit this worker."
                  />
                </>
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
      )}
    </Form>
  );
}
